import React, { useEffect, useState } from 'react';
import styles from './CareerPopup.module.css';

// --- FIREBASE & R2 IMPORTS ---
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { r2, R2_PUBLIC_URL } from "../../r2Client"; // Ensure this path is correct
import { PutObjectCommand } from "@aws-sdk/client-s3";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const BUCKET_NAME = "baglami-images";
const WEB3FORMS_ACCESS_KEY = "d1409213-917e-42aa-b0cc-c16dfa8e5a56";
const OWNER_EMAIL = "info@dimorapune.com";

const CareerPopup = ({ isOpen, onClose, jobTitle }) => {
  const [resume, setResume] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => (document.body.style.overflow = 'auto');
  }, [isOpen]);

  // --- CLOUDFLARE R2 UPLOAD LOGIC ---
  const uploadToR2 = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    // Using the 'careerpdfs' subfolder as requested
    const key = `careerpdfs/${fileName}`;

    const arrayBuffer = await file.arrayBuffer();
    const body = new Uint8Array(arrayBuffer);

    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key,
      Body: body,
      ContentType: file.type,
    });

    await r2.send(command);

    return {
      url: `${R2_PUBLIC_URL}/${key}`,
      key: key
    };
  };

  // --- EMAIL NOTIFICATION LOGIC ---
  const sendEmailNotification = async (appData, resumeUrl) => {
    const emailData = new FormData();
    emailData.append("access_key", WEB3FORMS_ACCESS_KEY);
    emailData.append("to_email", OWNER_EMAIL);
    emailData.append("from_name", "Career Portal (Popup)");
    emailData.append("subject", `New Application: ${jobTitle} - ${appData.firstName}`);

    emailData.append("Applied For", jobTitle);
    emailData.append("Full Name", `${appData.firstName} ${appData.lastName}`);
    emailData.append("Email Address", appData.email);
    emailData.append("Phone Number", appData.phone);
    emailData.append("Experience", appData.experience);
    emailData.append("Gender", appData.gender);
    emailData.append("Additional Message", appData.message || "None");
    emailData.append("Resume PDF", `Link: ${resumeUrl}`);

    try {
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: emailData
      });
    } catch (err) {
      console.error("Email Error:", err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed.");
      setResume(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be less than 5MB.");
      setResume(null);
      return;
    }
    setError("");
    setResume(file);
  };

  // --- FORM SUBMISSION ---
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      setError("Please upload your resume (PDF).");
      return;
    }

    setIsSubmitting(true);
    const formElement = e.target;
    const formData = new FormData(formElement);

    try {
      // 1. Upload to Cloudflare R2
      const uploadResult = await uploadToR2(resume);

      // 2. Prepare Application Data
      const applicationData = {
        appliedFor: jobTitle,
        firstName: formData.get("firstName"),
        lastName: formData.get("lastName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        experience: formData.get("experience"),
        gender: formData.get("gender"),
        message: formData.get("message") || "",
        resumeUrl: uploadResult.url,
        resumeKey: uploadResult.key,
        status: "new",
        createdAt: serverTimestamp(),
      };

      // 3. Save to Firestore
      await addDoc(collection(db, "applications"), applicationData);

      // 4. Send Email Notification
      await sendEmailNotification(applicationData, uploadResult.url);

      alert(`Application for ${jobTitle} submitted successfully!`);
      setResume(null);
      onClose();
    } catch (err) {
      console.error("Submission error:", err);
      alert("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>

        <h2 className={styles.title}>APPLY FOR THIS POSITION</h2>
        {jobTitle && <p className={styles.jobSubtitle}>Position: <strong>{jobTitle}</strong></p>}

        <form className={styles.form} onSubmit={handleFormSubmit}>
          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>First Name</label>
              <input name="firstName" type="text" required />
            </div>
            <div className={styles.inputGroup}>
              <label>Last Name</label>
              <input name="lastName" type="text" required />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Email Address</label>
              <input name="email" type="email" required />
            </div>
            <div className={styles.inputGroup}>
              <label>Phone Number</label>
              <input name="phone" type="tel" required />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Experience (Years)</label>
              <input name="experience" type="text" placeholder="e.g. 2 Years" required />
            </div>
            <div className={styles.inputGroup}>
              <label>Gender</label>
              <select name="gender" required className={styles.selectInput} defaultValue="">
                <option value="" disabled hidden>Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Additional Info</label>
              <textarea name="message" rows="5" placeholder="Tell us why you're a great fit..."></textarea>
            </div>
            <div className={styles.uploadCol}>
              <div className={styles.uploadBox}>
                <label htmlFor="resume-upload" className={styles.uploadLabel}>
                  {!resume ? "Upload Resume (PDF)" : resume.name}
                </label>
                <input
                  id="resume-upload"
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="application/pdf"
                />
              </div>
              {error && <p className={styles.errorText} style={{ color: 'red', fontSize: '11px', marginTop: '5px' }}>{error}</p>}
              <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CareerPopup;